import * as React from 'react'
import { Route, Routes } from 'react-router-dom'

import { CompensationsTabsDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { ModuleMeta } from 'modules/core/lib/navigation/types'
import { type NavigationRoutesFn, buildModuleRoute } from 'modules/core/lib/react-router'

import ImportResultPage from 'modules/compensations/pages/ImportResultPage'

import { buildCycleMeta } from './buildCycleMeta'

const RootPage = React.lazy(() => import('modules/compensations/pages/RootPage'))
const RedirectToCompensationsPage = React.lazy(
  () => import('modules/compensations/pages/RedirectToCompensationsPage')
)
const CompensationPage = React.lazy(() => import('modules/compensations/pages/CompensationPage'))
const CyclesRoot = React.lazy(() => import('modules/compensations/pages/CyclesRoot'))
const CyclesList = React.lazy(() => import('modules/compensations/pages/CyclesPage'))
const InhouseActivationPage = React.lazy(
  () => import('modules/payroll_bundle/pages/InhouseActivationPage')
)
const IncidenceNoteTab = React.lazy(
  () => import('modules/bookkeepers/pages/incidences/IncidenceNoteTab')
)
const NavigateToEmployeeUpdatesPage = React.lazy(
  () => import('modules/compensations/pages/NavigateToEmployeeUpdatesPage')
)
const IncidenceDetailPage = React.lazy(
  () => import('modules/payroll_bundle/pages/IncidenceDetailPage')
)
const IncidenceHistoryPage = React.lazy(
  () => import('modules/payroll_bundle/pages/IncidenceHistoryPage')
)
const IncidenceMessagePage = React.lazy(
  () => import('modules/payroll_bundle/pages/IncidenceMessagePage')
)
const TimeReimbursementPage = React.lazy(
  () => import('modules/compensations/pages/settings/TimeReimbursementsPage')
)
const ExpensesReimbursementsPage = React.lazy(
  () => import('modules/compensations/pages/settings/ExpensesReimbursementsPage')
)
const UniversalImporterEmployeeCompensationsPage = React.lazy(
  () => import('modules/compensations/pages/UniversalImporterEmployeeCompensationsPage')
)
const EmployeeUpdatesDataCollectionPage = React.lazy(
  () => import('modules/compensations/pages/EmployeeUpdatesDataCollectionPage')
)
export const compensationsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    buildModuleRoute({
      path: navigation.compensations.path,
      element: <RootPage />,
      handle: {
        module: 'compensations',
        moduleName: () => i18n.t('sidebar.compensation'),
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: CompensationsTabsDocument,
            variables: {
              companyId: session.company.id,
            },
          }).map(({ data }): ModuleMeta => {
            const findFeatureByName = (featureName: string) => {
              return data?.features?.featuresConnection?.nodes.find(
                (node) => node?.name === featureName
              )
            }

            const isCyclesManagementFeatureEnabled =
              findFeatureByName('dev_compensations_cycles_management')?.enabled ?? false

            const canSeeEmployeeUpdates =
              data?.compensations._policies.sidebarSeeCompensationsIncidencesSubLevelItem.can ??
              false

            return {
              title: i18n.t('sidebar.compensation'),
              settings: navigation.settings.compensations.fullPath(),
              subNavigation: [
                ...(!isCyclesManagementFeatureEnabled
                  ? [
                      {
                        title: i18n.t('compensation.routes.definition'),
                        href: navigation.compensations.definition.fullPath(),
                        isVisible:
                          data?.compensations._policies.sidebarSeeCompensationsTopLevelItem.can,
                      },
                    ]
                  : [
                      {
                        title: i18n.t('compensation.routes.cycles'),
                        href: navigation.compensations.cycles.list.fullPath(),
                        isVisible:
                          data?.compensations._policies.sidebarSeeCompensationsTopLevelItem.can,
                        index: true,
                      },
                    ]),
                {
                  title: i18n.t('compensation.routes.updates'),
                  href: navigation.compensations.employeeUpdatesDataCollection.fullPath(),
                  isVisible: canSeeEmployeeUpdates,
                },
                {
                  title: i18n.t('compensation.routes.employee_updates_activation'),
                  href: navigation.compensations.inhouseActivation.fullPath(),
                  isVisible:
                    data?.compensations._policies
                      .sidebarSeeCompensationsInHouseActivationSubLevelItem.can,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <RedirectToCompensationsPage />,
        },
        {
          path: navigation.compensations.cycles.path,
          element: <CyclesRoot />,
          children: [
            {
              path: navigation.compensations.cycles.list.path,
              element: <CyclesList />,
            },
            {
              path: navigation.compensations.cycles.id.path,
              element: <CompensationPage />,
              handle: {
                resource: true,
                meta(context) {
                  return buildCycleMeta(context, navigation)
                },
              },
              children: [
                {
                  path: navigation.compensations.cycles.id.importResult.path,
                  element: (
                    <Routes>
                      <Route
                        path=""
                        element={
                          <ImportResultPage
                            closePath={(id: string) =>
                              navigation.compensations.cycles.id.fullPath({ id })
                            }
                          />
                        }
                      />
                    </Routes>
                  ),
                },
                {
                  path: navigation.compensations.cycles.id.importCompensations.path,
                  element: (
                    <Routes>
                      <Route
                        path=""
                        element={
                          <UniversalImporterEmployeeCompensationsPage
                            closePath={(id: string) =>
                              navigation.compensations.cycles.id.fullPath({ id })
                            }
                          />
                        }
                      />
                    </Routes>
                  ),
                },
              ],
            },
          ],
        },

        {
          path: navigation.compensations.definition.path,
          element: <CompensationPage />,
          children: [
            {
              path: navigation.compensations.definition.importResult.path,
              element: (
                <Routes>
                  <Route
                    path=""
                    element={
                      <ImportResultPage
                        closePath={navigation.compensations.definition.fullPath()}
                      />
                    }
                  />
                </Routes>
              ),
            },
            {
              path: navigation.compensations.definition.importCompensations.path,
              element: (
                <Routes>
                  <Route
                    path=""
                    element={
                      <UniversalImporterEmployeeCompensationsPage
                        closePath={navigation.compensations.definition.fullPath()}
                      />
                    }
                  />
                </Routes>
              ),
            },
          ],
        },
        {
          path: navigation.compensations.employeeUpdates.path,
          element: <NavigateToEmployeeUpdatesPage />,
          children: [
            {
              index: true,
              path: navigation.compensations.employeeUpdates.id.path,
              element: <IncidenceDetailPage />,
            },
            {
              path: navigation.compensations.employeeUpdates.idChanges.path,
              element: <IncidenceHistoryPage />,
            },
            {
              path: navigation.compensations.employeeUpdates.idMessages.path,
              element: <IncidenceMessagePage />,
            },
            {
              path: navigation.compensations.employeeUpdates.idNotes.path,
              element: <IncidenceNoteTab />,
            },
          ],
        },
        {
          path: navigation.compensations.employeeUpdatesDataCollection.path,
          element: <EmployeeUpdatesDataCollectionPage />,
          children: [
            {
              index: true,
              path: navigation.compensations.employeeUpdatesDataCollection.id.path,
              element: <IncidenceDetailPage />,
            },
            {
              path: navigation.compensations.employeeUpdatesDataCollection.idChanges.path,
              element: <IncidenceHistoryPage />,
            },
            {
              path: navigation.compensations.employeeUpdatesDataCollection.idMessages.path,
              element: <IncidenceMessagePage />,
            },
            {
              path: navigation.compensations.employeeUpdatesDataCollection.idNotes.path,
              element: <IncidenceNoteTab />,
            },
          ],
        },
        {
          path: navigation.compensations.inhouseActivation.path,
          element: <InhouseActivationPage />,
        },
        {
          path: navigation.compensations.timeReimbursement.path,
          element: <TimeReimbursementPage />,
        },
        {
          path: navigation.compensations.expensesReimbursement.path,
          element: <ExpensesReimbursementsPage />,
        },
      ],
    }),
  ]
}
